import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, BookOpen } from "lucide-react";

const blank = () => ({ id: null, title: "", section: "", summary: "", body: "", read_min: 4 });

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/nutrition/articles").then(({ data }) => setArticles(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(blank()); setOpen(true); };
  const openEdit = async (id) => {
    try { const { data } = await api.get(`/nutrition/articles/${id}`); setForm(data); setOpen(true); }
    catch (e) { toast.error("Could not load article"); }
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/articles", {
        ...form, id: form.id || undefined, section: form.section || "General",
        read_min: parseInt(form.read_min) || 4,
      });
      toast.success(form.id ? "Article updated" : "Article created");
      setOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/articles/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-3 mt-4" data-testid="admin-articles">
      <Button data-testid="admin-new-article" onClick={openNew} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold"><Plus className="h-4 w-4 mr-1" /> New article</Button>

      {articles.map((a) => (
        <Card key={a.id} data-testid={`admin-article-${a.id}`} className="rounded-xl bg-card border-border p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5 text-[#ccff0c]" />
            <div>
              <p className="font-semibold text-sm">{a.title}</p>
              <Badge className="bg-secondary border border-border text-muted-foreground text-[10px] mt-0.5">{a.section}</Badge>
            </div>
          </div>
          <div className="flex gap-1">
            <button data-testid={`edit-article-${a.id}`} onClick={() => openEdit(a.id)} className="p-2 text-muted-foreground hover:text-[#ccff0c]"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`delete-article-${a.id}`} onClick={() => del(a.id)} className="p-2 text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        </Card>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover border-border max-w-[440px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title</Label><Input data-testid="af-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-card border-border h-10" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Section</Label><Input data-testid="af-section" value={form.section} onChange={(e) => set("section", e.target.value)} placeholder="Protein Basics" className="bg-card border-border h-10" /></div>
              <div><Label className="text-xs">Read (min)</Label><Input data-testid="af-read" type="number" value={form.read_min} onChange={(e) => set("read_min", e.target.value)} className="bg-card border-border h-10" /></div>
            </div>
            <div><Label className="text-xs">Summary</Label><Input data-testid="af-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} className="bg-card border-border h-10" /></div>
            <div><Label className="text-xs">Body</Label><Textarea data-testid="af-body" value={form.body} onChange={(e) => set("body", e.target.value)} className="bg-card border-border min-h-[160px]" /></div>
            <Button data-testid="af-save" onClick={save} disabled={saving} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">{saving ? "Saving…" : "Save article"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
